/**
 * The Holdfast reconciliation offer (D-335 D4; M7 spec §12.3, §12.5): at standing ≤ contracts.json
 * `reconciliation.atOrBelow` (−50, where every station of the faction refuses docking) the faction offers, over comms
 * only, to take `credits` and restore the standing to `restoreTo` (−40). It is repeatable (every time the standing
 * falls that low again, from any cause) and needs no docking: the Charter HUD shows it on the faction's docking
 * refusal, and a second docking request pays, like the fines flow (`fines.ts`).
 *
 * Pure Career commands; message rate.
 */
import { withStanding, withWallet } from '../career/ops'
import { fail, formatCredits, insufficientFunds, ok, type CmdResult } from '../career/result'
import type { Career } from '../career/types'
import type { ContractsConfig, SimFactionId } from '../data/m2Schemas'

export type ReconciliationConfig = ContractsConfig['reconciliation']

/** An open reconciliation offer: who offers it, the price and the standing it restores. */
export interface ReconciliationOffer {
  faction: SimFactionId
  credits: number
  /** The player's standing now (≤ `atOrBelow`). */
  standing: number
  restoreTo: number
}

/** The offer open to `career` (standing ≤ `atOrBelow` with the configured faction), or null. */
export function reconciliationOffer(cfg: ReconciliationConfig, career: Career): ReconciliationOffer | null {
  const standing = career.standings[cfg.faction]
  if (!(standing <= cfg.atOrBelow)) return null
  return { faction: cfg.faction, credits: Math.round(cfg.credits), standing, restoreTo: cfg.restoreTo }
}

/**
 * Accepts the offer: pays `credits` and sets the standing to `restoreTo` (one `standingChanged` effect). Refuses with
 * `contractUnavailable` when no offer is open and `insufficientFunds` when the wallet is short (nothing changes).
 */
export function acceptReconciliation(cfg: ReconciliationConfig, career: Career): CmdResult<Career> {
  const offer = reconciliationOffer(cfg, career)
  if (offer === null) return fail('contractUnavailable', 'No reconciliation is on offer.', { faction: cfg.faction })
  if (career.wallet < offer.credits) return insufficientFunds(offer.credits, career.wallet)
  const delta = offer.restoreTo - offer.standing
  const next = withStanding(withWallet(career, -offer.credits), offer.faction, delta)
  return ok(next, [{ kind: 'standingChanged', faction: offer.faction, delta, value: next.standings[offer.faction] }])
}

const FACTION_NAME: Readonly<Record<SimFactionId, string>> = {
  authority: 'Authority',
  tethys: 'Tethys',
  meridian: 'Meridian',
  varro_security: 'Varro',
  mutual: 'Mutual',
  holdfast: 'Holdfast',
  raider: 'Raider',
}

/** The comms line of an open offer (the second docking request pays). */
export function reconciliationOfferText(offer: ReconciliationOffer): string {
  return `${FACTION_NAME[offer.faction]} will talk: make amends for ${formatCredits(offer.credits)} (standing to ${offer.restoreTo}). Request docking again to pay.`
}

/** The comms line after an attempt. */
export function reconciliationResultText(r: CmdResult<Career>, faction: SimFactionId): string {
  return r.ok ? `${FACTION_NAME[faction]} accepts your amends. Docking rights restored.` : r.error.message
}
